import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import AdminDashboard from "./AdminDashboard";
import PremiumPlans from "./PremiumPlans";

const APP_NAME = "MuriAI";
const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3002";

// Global fetch interceptor to append JWT token automatically
const originalFetch = window.fetch;
window.fetch = function (url, options) {
  const token = sessionStorage.getItem("muri_token");
  if (token) {
    options = options || {};
    options.headers = options.headers || {};
    if (options.body && !(options.body instanceof FormData)) {
      options.headers["Content-Type"] = options.headers["Content-Type"] || "application/json";
    }
    options.headers["Authorization"] = `Bearer ${token}`;
  }
  return originalFetch(url, options);
};

const SUGGESTIONS = [
  { icon: "🥦", text: "Tell me about broccoli" },
  { icon: "🧠", text: "How does memory work?" },
  { icon: "🌍", text: "Explain climate change simply" },
  { icon: "🍅", text: "History of the tomato" },
];

// ── localStorage helpers (only for theme, non-sensitive prefs) ──
function getStored(key, def) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
}
function setStored(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// ── Backend API helpers ────────────────────────────────────────
async function apiRegister(username, password) {
  const res = await fetch(`${API_BASE}/api/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  return res.json();
}

async function apiLogin(username, password) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (data.success && data.token) {
    sessionStorage.setItem("muri_token", data.token);
    if (data.department) {
      sessionStorage.setItem("muri_user_department", data.department);
    } else {
      sessionStorage.removeItem("muri_user_department");
    }
    if (data.agentKey) {
      sessionStorage.setItem("muri_user_agent_key", data.agentKey.toString());
    } else {
      sessionStorage.removeItem("muri_user_agent_key");
    }
  }
  return data;
}

async function apiLoadHistory(username) {
  try {
    const res = await fetch(`${API_BASE}/api/history/${username}`);
    const data = await res.json();
    return data.chats || [];
  } catch { return []; }
}

async function apiSaveHistory(username, chats) {
  try {
    await fetch(`${API_BASE}/api/history/${username}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chats }),
    });
  } catch {}
}

async function apiDeleteHistory(username) {
  try {
    await fetch(`${API_BASE}/api/history/${username}`, { method: "DELETE" });
  } catch {}
}

function MuriAILogo({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <defs>
        <linearGradient id="ng" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#6ee7f7"/><stop offset="1" stopColor="#a78bfa"/>
        </linearGradient>
      </defs>
      <polygon points="20,3 37,12 37,28 20,37 3,28 3,12" stroke="url(#ng)" strokeWidth="2" fill="none"/>
      <polygon points="20,10 30,15.5 30,24.5 20,30 10,24.5 10,15.5" fill="url(#ng)" opacity="0.18"/>
      <circle cx="20" cy="20" r="4" fill="url(#ng)"/>
    </svg>
  );
}

function TypingDots() {
  return <div className="typing-dots"><span/><span/><span/></div>;
}

function formatMessage(text) {
  if (!text) return "";
  let html = text
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/\*\*(.*?)\*\*/g,"<strong>$1</strong>")
    .replace(/\*(.*?)\*/g,"<em>$1</em>")
    .replace(/`(.*?)`/g,"<code>$1</code>")
    .replace(/^### (.*$)/gm,"<h3>$1</h3>")
    .replace(/^## (.*$)/gm,"<h2>$1</h2>")
    .replace(/^# (.*$)/gm,"<h1>$1</h1>")
    .replace(/^- (.*$)/gm,"<li>$1</li>")
    .replace(/\n\n/g,"</p><p>")
    .replace(/\n/g,"<br/>");
  if (html.includes("<li>")) html = html.replace(/(<li>[\s\S]*?<\/li>)/g,"<ul>$1</ul>");
  return `<p>${html}</p>`;
}

// ── Agent Select Page (Active Fleet) ──────────────────────────
function AgentSelectPage({ agents, activeKey, username, onSelect }) {
  return (
    <div className="login-page">
      <div className="bg-grid"/>
      <div className="login-card" style={{maxWidth:"480px"}}>
        <div className="login-logo"><MuriAILogo size={44}/></div>
        <h1 className="login-title">Hi, <span className="gradient-text">{username}</span>!</h1>
        <p className="login-sub" style={{marginBottom:4}}>Active Fleet</p>
        <p style={{fontSize:11,color:"var(--text3)",marginBottom:16,textAlign:"center"}}>Choose an active agent to chat with:</p>
        {agents.length === 0 ? (
          <div style={{textAlign:"center",padding:"24px 0",color:"var(--text3)",fontSize:13}}>
            🚫 No active agents available at the moment.<br/>
            <span style={{fontSize:11,marginTop:8,display:"block"}}>Please contact your admin to activate an agent.</span>
          </div>
        ) : (
          <div style={{width:"100%",display:"flex",flexDirection:"column",gap:"10px",marginTop:"8px"}}>
            {agents.map(agent => (
              <button
                key={agent.id}
                onClick={() => onSelect(agent)}
                style={{
                  width:"100%", padding:"14px 18px",
                  background: agent.id === activeKey ? "rgba(99,102,241,0.15)" : "rgba(255,255,255,0.04)",
                  border: agent.id === activeKey ? "1.5px solid rgba(99,102,241,0.5)" : "1.5px solid var(--border)",
                  borderRadius:"12px", cursor:"pointer",
                  display:"flex", alignItems:"center", gap:"14px",
                  transition:"all 0.2s", textAlign:"left",
                }}
              >
                <span style={{fontSize:"28px"}}>{agent.icon || "🤖"}</span>
                <div>
                  <p style={{margin:0, fontWeight:700, fontSize:"14px", color:"var(--text)"}}>{agent.name}</p>
                  {agent.desc && <p style={{margin:0, fontSize:"12px", color:"var(--text3)", marginTop:"2px"}}>{agent.desc}</p>}
                  {agent.id === activeKey && <p style={{margin:0, fontSize:"11px", color:"#4ade80", marginTop:"3px"}}>● Recommended by admin</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Login Page ─────────────────────────────────────────────────
function LoginPage({ onLogin, onAdminLogin }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    if (!user.trim() || !pass.trim()) { setErr("Please fill in all fields."); return; }
    setBusy(true);
    setErr("");
    try {
      if (mode === "register") {
        const res = await apiRegister(user.trim(), pass);
        if (res.error) { setErr(res.error); return; }
        onLogin(user.trim());
      } else {
        const res = await apiLogin(user.trim(), pass);
        if (res.error) { setErr(res.error); return; }
        onLogin(user.trim());
      }
    } catch {
      setErr("Cannot connect to server. Make sure backend is running on port 3001.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="bg-grid"/>
      <div className="login-card">
        <div className="login-logo"><MuriAILogo size={48}/></div>
        <h1 className="login-title">Welcome to <span className="gradient-text">MuriAI</span></h1>
        <p className="login-sub">Intelligence, refined.</p>
        <div className="login-tabs">
          <button className={mode==="login"?"active":""} onClick={()=>{setMode("login");setErr("")}}>Sign In</button>
          <button className={mode==="register"?"active":""} onClick={()=>{setMode("register");setErr("")}}>Register</button>
          <button className="admin-tab-btn" onClick={onAdminLogin} title="Admin Login">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Admin
          </button>
        </div>
        <input className="login-input" placeholder="Username" value={user} onChange={e=>setUser(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} disabled={busy}/>
        <input className="login-input" type="password" placeholder="Password" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} disabled={busy}/>
        {err && <p className="login-err">{err}</p>}
        <button className="login-btn" onClick={handle} disabled={busy}>
          {busy ? "Please wait..." : mode==="login" ? "Sign In" : "Create Account"}
        </button>
      </div>
    </div>
  );
}

// ── Admin Login Page ───────────────────────────────────────────
function AdminLoginPage({ onAdminLogin, onBack }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    if (!user.trim() || !pass.trim()) { setErr("Please fill in all fields."); return; }
    setBusy(true);
    setErr("");
    try {
      const res = await apiLogin(user.trim(), pass);
      if (res.error) {
        setErr(res.error);
      } else if (res.role === "admin") {
        onAdminLogin();
      } else {
        setErr("Access denied: Not an admin.");
      }
    } catch (e) {
      setErr("Login failed: Cannot connect to server.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="bg-grid"/>
      <div className="login-card">
        <div className="login-logo">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="url(#ag)" strokeWidth="1.5">
            <defs>
              <linearGradient id="ag" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                <stop stopColor="#6ee7f7"/><stop offset="1" stopColor="#a78bfa"/>
              </linearGradient>
            </defs>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </div>
        <h1 className="login-title">Admin <span className="gradient-text">Portal</span></h1>
        <p className="login-sub">Restricted access — admins only.</p>
        <input className="login-input" placeholder="Admin Username" value={user} onChange={e=>setUser(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} disabled={busy}/>
        <input className="login-input" type="password" placeholder="Admin Password" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} disabled={busy}/>
        {err && <p className="login-err">{err}</p>}
        <button className="login-btn" onClick={handle} disabled={busy} style={{background:"linear-gradient(135deg,#6ee7f7,#a78bfa)"}}>
          {busy ? "Verifying..." : "Access Admin Dashboard"}
        </button>
        <button className="admin-login-btn" onClick={onBack} style={{marginTop:"10px",background:"transparent",border:"1px solid var(--border)",color:"var(--text2)"}}>
          ← Back to User Login
        </button>
      </div>
    </div>
  );
}

function Message({ msg, index, onCopy, onSpeak, speaking }) {
  const isUser = msg.role === "user";
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { onCopy(msg.content); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className={`message-row ${isUser?"user-row":"assistant-row"}`} style={{animationDelay:`${index*0.04}s`}}>
      {!isUser && <div className="avatar muri-avatar"><MuriAILogo size={18}/></div>}
      <div className={`bubble ${isUser?"user-bubble":"assistant-bubble"}`}>
        {msg.typing ? <TypingDots/> : (
          <>
            <div className="message-text" dangerouslySetInnerHTML={{__html:formatMessage(msg.content)}}/>
            <div className="msg-actions">
              <button className="msg-action-btn" onClick={handleCopy}>
                {copied ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
                {copied ? "Copied!" : "Copy"}
              </button>
              {!isUser && (
                <button className={`msg-action-btn ${speaking?"speaking":""}`} onClick={()=>onSpeak(msg.content)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                  </svg>
                  {speaking ? "Stop" : "Speak"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {isUser && <div className="avatar user-avatar"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg></div>}
    </div>
  );
}

function PdfBanner({ pdfs, onUpload, uploading }) {
  const fileRef = useRef();
  return (
    <div className={`pdf-banner ${pdfs.length>0?"loaded":""}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
      </svg>
      {pdfs.length > 0 ? (
        <div className="pdf-list">
          {pdfs.map((p,i) => <span key={i} className="pdf-tag">{p.fileName} <em>{p.pages}p</em></span>)}
        </div>
      ) : (
        <span>{uploading ? "Processing PDF..." : "Upload PDFs to chat with their content"}</span>
      )}
      {!uploading && <button className="pdf-upload-btn" onClick={()=>fileRef.current.click()}>+ Add PDF</button>}
      <input ref={fileRef} type="file" accept=".pdf" multiple style={{display:"none"}}
        onChange={e=>[...e.target.files].forEach(f=>onUpload(f))}/>
    </div>
  );
}

// Extract first name only — strips email domain if present
function displayName(username) {
  if (!username) return "";
  const name = username.includes("@") ? username.split("@")[0] : username;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// ── Main App ───────────────────────────────────────────────────
export default function App() {
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    () => window.location.pathname.startsWith("/admin") || sessionStorage.getItem('muri_admin') === '1'
  );

  const [loggedIn, setLoggedIn] = useState(() => !!sessionStorage.getItem('muri_logged_user'));
  const [currentUser, setCurrentUser] = useState(() => sessionStorage.getItem('muri_logged_user') || "");
  const [activeAgentName, setActiveAgentName] = useState("");
  const [selectedAgentKey, setSelectedAgentKey] = useState(() => {
    const stored = sessionStorage.getItem('muri_active_agent_key');
    return stored ? parseInt(stored) : null;
  });
  const [agentList, setAgentList] = useState([]);
  const [showAgentSelect, setShowAgentSelect] = useState(false);
  const [showAgentDeactivatedPopup, setShowAgentDeactivatedPopup] = useState(false);
  const [availableAgentsForSwitch, setAvailableAgentsForSwitch] = useState([]);
  const [theme, setTheme] = useState(() => getStored("muri_theme", "dark"));
  const [messages, setMessages] = useState([]);
  const [allChats, setAllChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(() => {
    const stored = sessionStorage.getItem('muri_active_chat_id');
    return stored ? parseInt(stored) : null;
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedAgentKey) {
      sessionStorage.setItem('muri_active_agent_key', selectedAgentKey);
    } else {
      sessionStorage.removeItem('muri_active_agent_key');
    }
  }, [selectedAgentKey]);

  useEffect(() => {
    if (activeChatId) {
      sessionStorage.setItem('muri_active_chat_id', activeChatId);
    } else {
      sessionStorage.removeItem('muri_active_chat_id');
    }
  }, [activeChatId]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pdfs, setPdfs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [speechPaused, setSpeechPaused] = useState(false);
  const [listening, setListening] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const menuRef = useRef(null);
  const recognitionRef = useRef(null);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { document.body.className = theme; }, [theme]);

  useEffect(() => {
    const user = sessionStorage.getItem('muri_logged_user');
    if (user) {
      handleLogin(user);
    }
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/pdf-status`)
      .then(r => r.json())
      .then(data => { if (data.loaded) setPdfs([{ fileName: data.fileName, pages: "?", chunks: data.chunks }]); })
      .catch(() => {});
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({behavior:"smooth"}); }, [messages]);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight,160)+"px";
    }
  }, [input]);

  // ── Poll every 10s to detect if current agent got deactivated ──
  useEffect(() => {
    if (!loggedIn || !selectedAgentKey) return;
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/agents`);
        const data = await res.json();
        const activeAgents = (data.agents || []).map(a => ({ ...a, id: Number(a.id) }));
        setAgentList(activeAgents);
        // Check if currently selected agent is still active
        const stillActive = activeAgents.find(a => Number(a.id) === Number(selectedAgentKey));
        if (!stillActive) {
          // Agent got deactivated — show popup
          setAvailableAgentsForSwitch(activeAgents);
          setShowAgentDeactivatedPopup(true);
        }
      } catch {}
    }, 10000); // every 10 seconds
    return () => clearInterval(poll);
  }, [loggedIn, selectedAgentKey]);


  const handleLogin = async (username) => {
    sessionStorage.setItem('muri_logged_user', username);
    setCurrentUser(username);
    const chats = await apiLoadHistory(username);
    setAllChats(chats);
    // Load subscription
    try {
      const sr = await fetch(`${API_BASE}/api/subscription/${username}`);
      const sd = await sr.json();
      setSubscription(sd.subscription || null);
    } catch {}

    // Load active chat messages if we had one active
    const storedChatId = sessionStorage.getItem('muri_active_chat_id');
    if (storedChatId) {
      const activeId = parseInt(storedChatId);
      const activeChat = chats.find(c => c.id === activeId);
      if (activeChat) {
        setMessages(activeChat.messages || []);
        setActiveChatId(activeId);
      }
    }

    const storedAgentKey = sessionStorage.getItem('muri_active_agent_key');
    const deptAgentKeyStr = sessionStorage.getItem("muri_user_agent_key");
    const activeAgentKeyStr = deptAgentKeyStr || storedAgentKey;

    if (activeAgentKeyStr) {
      const activeKey = parseInt(activeAgentKeyStr);
      setSelectedAgentKey(activeKey);
      try {
        const res = await fetch(`${API_BASE}/api/agents`);
        const data = await res.json();
        const agents = data.agents || [];
        setAgentList(agents);
        const match = agents.find(a => Number(a.id) === Number(activeKey));
        if (match) setActiveAgentName(match.name);
      } catch {}
      setShowAgentSelect(false);
      setLoggedIn(true);
    } else {
      // General user flow - select active agent
      try {
        const res = await fetch(`${API_BASE}/api/agents`);
        const data = await res.json();
        const agents = data.agents || [];
        setAgentList(agents);
        const activeKey = data.activeKey;
        if (activeKey) {
          const active = agents.find(a => Number(a.id) === Number(activeKey));
          if (active) { setSelectedAgentKey(activeKey); setActiveAgentName(active.name); }
        }
        if (agents.length === 0) {
          setShowAgentSelect(true);
        } else if (agents.length === 1) {
          setSelectedAgentKey(agents[0].id);
          setActiveAgentName(agents[0].name);
          setShowAgentSelect(true);
        } else {
          setShowAgentSelect(true);
        }
      } catch {
        setLoggedIn(true);
      }
    }
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgentKey(agent.id);
    setActiveAgentName(agent.name);
    setShowAgentSelect(false);
    setLoggedIn(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('muri_logged_user');
    sessionStorage.removeItem('muri_token');
    sessionStorage.removeItem('muri_user_department');
    sessionStorage.removeItem('muri_user_agent_key');
    sessionStorage.removeItem('muri_active_agent_key');
    sessionStorage.removeItem('muri_active_chat_id');
    sessionStorage.removeItem('muri_admin');
    setLoggedIn(false);
    setCurrentUser("");
    setSelectedAgentKey(null);
    setActiveAgentName("");
    setMessages([]);
    setActiveChatId(null);
    setAllChats([]);
    document.getElementById('muri-landing').style.display = 'flex';
    document.getElementById('root').style.display = 'none';
  };

  const toggleTheme = () => {
    const t = theme === "dark" ? "light" : "dark";
    setTheme(t);
    setStored("muri_theme", t);
  };

  // ── Chat history ────────────────────────────────────────────
  const saveChats = async (chats) => {
    setAllChats(chats);
    await apiSaveHistory(currentUser, chats);
  };

  const newChat = () => { setMessages([]); setActiveChatId(null); setInput(""); };
  const loadChat = (chat) => { setMessages(chat.messages); setActiveChatId(chat.id); };

  const deleteChat = async (id, e) => {
    e.stopPropagation();
    const updated = allChats.filter(c => c.id !== id);
    await saveChats(updated);
    if (activeChatId === id) newChat();
  };

  // ── Clear All History ───────────────────────────────────────
  const handleClearHistory = async () => {
    if (!window.confirm("Clear all chat history? This cannot be undone.")) return;
    setClearingHistory(true);
    try {
      await apiDeleteHistory(currentUser);
      setAllChats([]);
      newChat();
    } catch (err) {
      alert("Failed to clear history: " + err.message);
    } finally {
      setClearingHistory(false);
    }
  };

  // ── PDF Upload ─────────────────────────────────────────────
  const handlePdfUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("pdf", file);
    try {
      const res = await fetch(`${API_BASE}/api/upload`, {method:"POST", body:formData});
      const data = await res.json();
      if (data.success) {
        setPdfs(prev => [...prev, {fileName:data.fileName, pages:data.pages, chunks:data.chunks}]);
        setMessages(prev => [...prev, {role:"assistant", content:`✅ **PDF loaded: "${data.fileName}"**\n\n${data.pages} pages ready!`}]);
      } else alert("Upload failed: " + data.error);
    } catch(err) { alert("Upload error: " + err.message); }
    finally { setUploading(false); }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement("textarea"); ta.value = text;
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    });
  };


  const handleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input requires a browser with Web Speech API (Chrome, Edge, or Safari)."); return; }
    if (listening) { 
      try { recognitionRef.current?.stop(); } catch {}
      setListening(false); 
      return; 
    }

    try {
      const recognition = new SR();
      recognitionRef.current = recognition;
      // ta-IN accurately captures spoken Tamil as well as English words
      recognition.lang = "ta-IN";
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;

      let capturedText = "";

      recognition.onstart = () => {
        setListening(true);
      };

      recognition.onresult = (e) => {
        let finalStr = "";
        let interimStr = "";
        for (let i = 0; i < e.results.length; i++) {
          if (e.results[i].isFinal) {
            finalStr += e.results[i][0].transcript + " ";
          } else {
            interimStr += e.results[i][0].transcript;
          }
        }
        const fullTranscript = (finalStr + interimStr).trim();
        if (fullTranscript) {
          capturedText = fullTranscript;
          setInput(fullTranscript); // live display into input textarea
          recognition._lastTranscript = fullTranscript;
        }
      };

      recognition.onend = () => {
        setListening(false);
        const textToSend = (capturedText || recognition._lastTranscript || "").trim();
        if (textToSend) {
          setInput(textToSend);
          setTimeout(() => {
            sendMessage(textToSend);
          }, 200);
        }
      };

      recognition.onerror = (err) => {
        console.warn("Speech recognition error:", err.error);
        setListening(false);
        if (err.error === "not-allowed" || err.error === "service-not-allowed") {
          alert("Microphone permission denied. Please allow microphone access in your browser settings.");
        }
      };

      recognition.start();
    } catch (err) {
      console.error("Mic start error:", err);
      setListening(false);
    }
  };

  // ── speakText — uses Murf AI (Juliet Conversational) with smooth fallback ───
  const speakText = async (text) => {
    if (!text) return;
    
    // Stop any currently playing audio
    if (window._muriAudio) {
      try { window._muriAudio.pause(); window._muriAudio.currentTime = 0; } catch {}
      window._muriAudio = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();

    // Strip emojis, markdown symbols, and bullet points so TTS speaks naturally without pronouncing emoji descriptions
    const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E6}-\u{1F1FF}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{200D}\u{FE0F}]/gu;
    const plain = text
      .replace(emojiRegex, "")
      .replace(/#{1,3} /g, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/`(.*?)`/g, "$1")
      .replace(/<[^>]+>/g, "")
      .replace(/•/g, "")
      .replace(/\n+/g, ". ")
      .replace(/\s{2,}/g, " ")
      .trim();
    if (!plain) return;

    setSpeakingId(text.slice(0, 20));
    setSpeechPaused(false);

    // Try Murf AI voice first
    try {
      const res = await fetch(`${API_BASE}/api/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: plain })
      });
      const data = await res.json();
      if (data.success && (data.audioUrl || data.encodedAudio)) {
        const audioSrc = data.audioUrl || `data:audio/mp3;base64,${data.encodedAudio}`;
        const audio = new Audio(audioSrc);
        window._muriAudio = audio;
        audio.onplay = () => { setSpeakingId(text.slice(0, 20)); setSpeechPaused(false); };
        audio.onended = () => { setSpeakingId(null); setSpeechPaused(false); window._muriAudio = null; };
        audio.onerror = () => { doBrowserSpeak(plain, text); };
        await audio.play();
        return;
      }
    } catch (err) {
      console.warn("Murf AI audio playback fallback:", err);
    }

    // Fallback to browser SpeechSynthesis if Murf is unreachable
    doBrowserSpeak(plain, text);
  };

  const doBrowserSpeak = (plain, rawText) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const isTamil = /[\u0B80-\u0BFF]/.test(plain);
    const doSpeak = () => {
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(plain);
      utt.rate = isTamil ? 0.92 : 1;
      utt.pitch = 1;
      utt.volume = 1;
      const voices = window.speechSynthesis.getVoices();
      let preferred = null;
      if (isTamil) {
        preferred = voices.find(v => v.lang.startsWith("ta") || v.name.toLowerCase().includes("tamil")) ||
                    voices.find(v => v.lang.startsWith("en-IN")) ||
                    voices[0];
        utt.lang = "ta-IN";
      } else {
        preferred = voices.find(v => v.lang.startsWith("en-IN") || (v.lang.startsWith("en") && (v.name.includes("Google") || v.name.includes("Microsoft")))) ||
                    voices.find(v => v.lang.startsWith("en")) ||
                    voices[0];
        utt.lang = "en-IN";
      }
      if (preferred) utt.voice = preferred;
      utt.onstart = () => { setSpeakingId((rawText || plain).slice(0, 20)); setSpeechPaused(false); };
      utt.onend = () => { setSpeakingId(null); setSpeechPaused(false); };
      utt.onerror = () => { setSpeakingId(null); setSpeechPaused(false); };
      window.speechSynthesis.speak(utt);
      setSpeakingId((rawText || plain).slice(0, 20));
      setSpeechPaused(false);
    };
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => { doSpeak(); window.speechSynthesis.onvoiceschanged = null; };
    } else {
      doSpeak();
    }
  };

  // ── handleSpeak — pause/resume/stop toggle for Murf & Browser Audio ───
  const handleSpeak = (text) => {
    if (window._muriAudio) {
      if (window._muriAudio.paused) {
        window._muriAudio.play().catch(() => {});
        setSpeechPaused(false);
        return;
      } else {
        window._muriAudio.pause();
        setSpeechPaused(true);
        return;
      }
    }
    const ss = window.speechSynthesis;
    if (ss && ss.paused) {
      ss.resume();
      setSpeechPaused(false);
      return;
    }
    if (ss && ss.speaking) {
      ss.pause();
      setSpeechPaused(true);
      return;
    }
    speakText(text);
  };

  const exportChat = () => {
    const lines = messages.map(m=>`[${m.role.toUpperCase()}]\n${m.content}`).join("\n\n---\n\n");
    const blob = new Blob([`MuriAI Chat Export\n${"=".repeat(40)}\n\n${lines}`],{type:"text/plain"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`muri-chat-${Date.now()}.txt`; a.click();
  };

  const exportChatPDF = () => {
    const content = messages.map(m=>`[${m.role.toUpperCase()}]\n${m.content}`).join("\n\n");
    const win = window.open("","_blank");
    win.document.write(`<html><head><title>MuriAI Chat</title><style>body{font-family:sans-serif;padding:32px;max-width:800px;margin:0 auto;line-height:1.6}pre{white-space:pre-wrap;background:#f5f5f5;padding:16px;border-radius:8px}</style></head><body><h2>MuriAI Chat Export</h2><pre>${content}</pre></body></html>`);
    win.document.close(); win.print();
  };

  // ── Send Message (with emotion detection) ─────────────────
  const sendMessage = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;
    setInput("");
    const userMsg = {role:"user", content};
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);

    // Check for emotion/greeting first (from backend DB)
    try {
      const emotionRes = await fetch(`${API_BASE}/api/emotions`);
      const emotionData = await emotionRes.json();
      const emotionMap = emotionData.emotions || {};
      const lower = content.trim().toLowerCase().replace(/[!?.,;:]+/g, "").trim();

      // ── Detect greeting prefix + PDF question ─────────────
      // e.g. "hi what is purpose" → greetingReply + PDF answer merged
      let greetingPrefix = null;
      let pdfQuestion = null;

      // Check if message STARTS with an emotion key followed by more content
      for (const key of Object.keys(emotionMap)) {
        const keyClean = key.toLowerCase().replace(/[!?.,;:]+/g, "").trim();
        if (lower.startsWith(keyClean + " ") && lower.length > keyClean.length + 1) {
          greetingPrefix = emotionMap[key];
          pdfQuestion = content.trim().slice(keyClean.length).trim();
          break;
        }
      }

      // Pure emotion match (exact or very short ≤2 words)
      let emotionReply = emotionMap[lower] || null;
      if (!emotionReply && !greetingPrefix) {
        for (const key of Object.keys(emotionMap)) {
          const wordCount = lower.split(/\s+/).length;
          const isShortGreeting = wordCount <= 2;
          if (lower === key || (isShortGreeting && (lower.startsWith(key + " ") || lower.endsWith(" " + key)))) {
            emotionReply = emotionMap[key];
            break;
          }
        }
      }

      // Short gibberish (≤3 chars) with no match → friendly fallback
      if (!emotionReply && !greetingPrefix && content.trim().length <= 3) {
        emotionReply = "Hey! 👋 I didn't quite catch that. Could you type your question?";
      }

      // ── CASE 1: Greeting + PDF question → fetch PDF then merge ──
      if (greetingPrefix && pdfQuestion) {
        setLoading(true);
        const typingId = Date.now();
        setMessages(prev=>[...prev,{id:typingId,role:"assistant",content:"",typing:true}]);
        try {
          const res = await fetch(`${API_BASE}/api/chat`, {
            method:"POST",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify({username:currentUser, agentKey:selectedAgentKey, messages:[...newMessages.slice(0,-1), {role:"user", content:pdfQuestion}]})
          });
          const data = await res.json();
          const pdfReply = data.reply || "";
          // Merge: greeting intro + PDF answer
          const merged = greetingPrefix + "\n\n" + pdfReply;
          const words = merged.split(" ");
          let current = "";
          for (const word of words) {
            current += (current ? " " : "") + word;
            setMessages(prev=>prev.map(m=>m.id===typingId?{id:typingId,role:"assistant",content:current,typing:false}:m));
            await new Promise(r=>setTimeout(r,35));
          }
          setMessages(prev=>prev.map(m=>m.id===typingId?{id:typingId,role:"assistant",content:merged,typing:false}:m));
          speakText(merged);
          const finalMessages = newMessages.concat({role:"assistant",content:merged});
          const chatId = activeChatId || Date.now();
          const title = content.slice(0,36)+(content.length>36?"…":"");
          await saveChats([{id:chatId,title,messages:finalMessages,updatedAt:Date.now()},...allChats.filter(c=>c.id!==chatId)]);
          setActiveChatId(chatId);
        } catch(err) {
          setMessages(prev=>prev.map(m=>m.id===typingId?{id:typingId,role:"assistant",content:"Connection error: "+err.message,typing:false}:m));
        } finally { setLoading(false); }
        return;
      }

      // ── CASE 2: Pure emotion reply ────────────────────────
      if (emotionReply) {
        setLoading(true);
        const typingId = Date.now();
        setMessages(prev=>[...prev,{id:typingId,role:"assistant",content:"",typing:true}]);
        await new Promise(r => setTimeout(r, 400));
        const words = emotionReply.split(" ");
        let current = "";
        for (const word of words) {
          current += (current ? " " : "") + word;
          setMessages(prev=>prev.map(m=>m.id===typingId?{id:typingId,role:"assistant",content:current,typing:false}:m));
          await new Promise(r=>setTimeout(r,40));
        }
        const finalMessages = newMessages.concat({role:"assistant",content:emotionReply});
        setMessages(prev=>prev.map(m=>m.id===typingId?{id:typingId,role:"assistant",content:emotionReply,typing:false}:m));
        speakText(emotionReply);
        setLoading(false);
        return;
      }
    } catch {}

    setLoading(true);
    const typingId = Date.now();
    setMessages(prev=>[...prev,{id:typingId,role:"assistant",content:"",typing:true}]);
    try {
      const res = await fetch(`${API_BASE}/api/chat`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({username:currentUser, agentKey:selectedAgentKey, messages:newMessages.map(m=>({role:m.role,content:m.content}))})
      });
      const data = await res.json();
      const reply = data.reply || data.error || "Something went wrong.";
      const words = reply.split(" ");
      let current = "";
      setMessages(prev=>prev.map(m=>m.id===typingId?{id:typingId,role:"assistant",content:"",typing:false}:m));
      // 🔊 voice fires immediately while text streams in background
      speakText(reply);
      for (let i=0; i<words.length; i++) {
        current += (i===0?"":(" ")) + words[i];
        const snap = current;
        setMessages(prev=>prev.map(m=>m.id===typingId?{id:typingId,role:"assistant",content:snap,typing:false}:m));
        await new Promise(r=>setTimeout(r,40));
      }
      const finalMessages = newMessages.concat({role:"assistant",content:reply});
      setMessages(prev=>prev.map(m=>m.id===typingId?{role:"assistant",content:reply}:m));
      const chatId = activeChatId || Date.now();
      const title = content.slice(0,36)+(content.length>36?"…":"");
      await saveChats([{id:chatId,title,messages:finalMessages,updatedAt:Date.now()},...allChats.filter(c=>c.id!==chatId)]);
      setActiveChatId(chatId);
    } catch(err) {
      setMessages(prev=>prev.map(m=>m.id===typingId?{role:"assistant",content:"Connection error: "+err.message}:m));
    } finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();} };

  // ── Render guards ────────────────────────────────────────
  if (window.location.pathname === "/admin-login" || showAdminLogin) {
    return (
      <AdminLoginPage
        onAdminLogin={() => {
          sessionStorage.setItem('muri_admin', '1');
          setIsAdmin(true);
          setShowAdminLogin(false);
          window.location.href = "/admin";
        }}
        onBack={() => {
          setShowAdminLogin(false);
          if (window.location.pathname === "/admin-login") {
            window.location.href = "/";
          }
        }}
      />
    );
  }

  if ((window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin-login") || isAdmin) {
    return (
      <AdminDashboard
        onLogout={() => {
          sessionStorage.removeItem('muri_admin');
          sessionStorage.removeItem('muri_token');
          sessionStorage.removeItem('muri_logged_user');
          setIsAdmin(false);
          window.location.href = "/";
        }}
      />
    );
  }

  if (!loggedIn && !showAgentSelect) return <LoginPage onLogin={handleLogin} onAdminLogin={() => setShowAdminLogin(true)}/>;
  if (showAgentSelect) return <AgentSelectPage agents={agentList} activeKey={selectedAgentKey} username={displayName(currentUser)} onSelect={handleAgentSelect}/>;
  const isNew = messages.length === 0;

  return (
    <div className={`app ${theme}`}>
      <div className="bg-grid"/>
      <aside className={`sidebar ${sidebarOpen?"open":"closed"}`}>
        <div className="sidebar-top">
          <div className="sidebar-brand"><MuriAILogo size={22}/><span className="brand-name">{APP_NAME}</span></div>
          <button className="icon-btn" onClick={()=>setSidebarOpen(false)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 19l-7-7 7-7"/></svg>
          </button>
        </div>
        <button className="new-chat-btn" onClick={newChat}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New conversation
        </button>

        <div className="sidebar-section">
          {allChats.length>0 && <p className="sidebar-label">History</p>}
          {allChats.map(chat=>(
            <div key={chat.id} className={`history-item ${chat.id===activeChatId?"active":""}`} onClick={()=>loadChat(chat)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              <span>{chat.title}</span>
              <button className="del-btn" onClick={(e)=>deleteChat(chat.id,e)}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          ))}
          {allChats.length > 0 && (
            <button
              className="clear-history-btn"
              onClick={handleClearHistory}
              disabled={clearingHistory}
              title="Clear all chat history"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
              {clearingHistory ? "Clearing..." : "Clear History"}
            </button>
          )}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-footer-actions">
            <button className="footer-btn" onClick={toggleTheme}>
              {theme==="dark"?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
              {theme==="dark"?"Light Mode":"Dark Mode"}
            </button>
            {messages.length>0&&<button className="footer-btn" onClick={exportChatPDF}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export Chat</button>}
          </div>
          <div className="user-card">
            <div className="user-dot">{displayName(currentUser)[0]?.toUpperCase()}</div>
            <div>
              <p className="user-name">{displayName(currentUser)}</p>
              <p className="user-plan">{subscription ? subscription.plan_name : "Free"} · {activeAgentName || "MuriAI"}</p>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
          <button
            onClick={() => setShowPlans(true)}
            style={{
              width: "100%", marginTop: 8, padding: "9px 0",
              background: subscription ? "rgba(99,102,241,0.12)" : "linear-gradient(135deg,rgba(99,102,241,0.3),rgba(139,92,246,0.3))",
              border: "1px solid rgba(99,102,241,0.35)",
              borderRadius: 8, color: "#a5b4fc", fontWeight: 600,
              fontSize: 12, cursor: "pointer", letterSpacing: "0.04em",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            ⭐ {subscription ? `${subscription.plan_name} Plan` : "Upgrade to Premium"}
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          {!sidebarOpen&&(<><button className="icon-btn" onClick={()=>setSidebarOpen(true)}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg></button><div className="topbar-brand"><MuriAILogo size={20}/><span>{APP_NAME}</span></div></>)}
          <div className="topbar-right">
            {speakingId && (
              <button onClick={()=>{if(window._muriAudio){try{window._muriAudio.pause();window._muriAudio.currentTime=0;}catch{}}window._muriAudio=null;if(window.speechSynthesis)window.speechSynthesis.cancel();setSpeakingId(null);setSpeechPaused(false);}}
                style={{display:"flex",alignItems:"center",gap:6,background:"rgba(99,102,241,0.15)",border:"1px solid rgba(99,102,241,0.4)",borderRadius:20,padding:"4px 12px",color:"#a5b4fc",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                <span style={{display:"flex",gap:2,alignItems:"flex-end",height:14}}>
                  {[1,1.6,1,1.8,1].map((h,i)=>(
                    <span key={i} style={{width:2,height:h*6,background:"#a5b4fc",borderRadius:2,display:"inline-block",animation:speechPaused?"none":`voiceBar 0.7s ease-in-out ${i*0.1}s infinite alternate`}}/>
                  ))}
                </span>
                {speechPaused ? "Paused · Click to stop" : "Speaking… · Click to stop"}
              </button>
            )}
            <button className="icon-btn" onClick={toggleTheme}>
              {theme==="dark"?<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
            </button>
            <div className="model-pill">
              <span className="model-dot"/>
              {pdfs.length>0?`${pdfs.length} PDF${pdfs.length>1?"s":""} loaded`:"MuriAI Intelligence"}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
        </header>

        <div className="chat-area">
          {isNew?(
            <div className="welcome">
              <div className="welcome-logo-wrap"><MuriAILogo size={52}/><div className="welcome-glow"/></div>
              <h1 className="welcome-title">Hello, <span className="gradient-text">{displayName(currentUser)}</span>!</h1>
              <p className="welcome-sub">{activeAgentName ? `I'm ${activeAgentName} — how can I help you today?` : "What can I help you with today?"}</p>
              <div className="suggestions">
                {SUGGESTIONS.map((s,i)=>(
                  <button key={i} className="suggestion-card" onClick={()=>sendMessage(s.text)} style={{animationDelay:`${0.1+i*0.07}s`}}>
                    <span className="suggestion-icon">{s.icon}</span><span>{s.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ):(
            <div className="messages">
              {messages.map((msg,i)=>(<Message key={i} msg={msg} index={i} onCopy={handleCopy} onSpeak={handleSpeak} speaking={speakingId===msg.content?.slice(0,20)}/>))}
              <div ref={bottomRef}/>
            </div>
          )}
        </div>

        <div className="input-area">
          <div className="input-box">
            <div className="input-plus-wrap" ref={menuRef}>
              <button className="plus-btn" onClick={()=>setShowMenu(v=>!v)} title="Add">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
              {showMenu && (
                <div className="plus-menu">
                  <button className="plus-menu-item" onClick={()=>{handleMic();setShowMenu(false);}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                    Voice input
                  </button>
                  <button className="plus-menu-item" onClick={()=>{exportChat();setShowMenu(false);}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Export chat
                  </button>
                  <button className="plus-menu-item" onClick={()=>{newChat();setShowMenu(false);}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    New conversation
                  </button>
                </div>
              )}

            </div>
            <textarea ref={textareaRef} className="input-field"
              placeholder="Ask anything..."
              value={input} onChange={e=>setInput(e.target.value)} onKeyDown={handleKeyDown} rows={1} disabled={loading}/>
            <div className="input-bottom">
              <div className="input-toolbar">
                <button className={`toolbar-btn mic-btn ${listening?"listening":""}`} onClick={handleMic} title="Voice input">
                  {listening?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>}
                  {listening?"Listening...":"Voice"}
                </button>
                <button className="toolbar-btn" onClick={()=>{const last=[...messages].reverse().find(m=>m.role==="assistant"&&!m.typing);if(last)handleCopy(last.content);}} disabled={!messages.some(m=>m.role==="assistant"&&!m.typing)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copy reply
                </button>
                <button className={`toolbar-btn ${speakingId?"speaking":""}`} onClick={()=>{const last=[...messages].reverse().find(m=>m.role==="assistant"&&!m.typing);if(last)handleSpeak(last.content);}} disabled={!messages.some(m=>m.role==="assistant"&&!m.typing)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                  {speechPaused ? "Resume" : speakingId ? "Pause" : "Speak"}
                </button>
              </div>
              <button className={`send-btn ${input.trim()?"active":""}`} onClick={()=>sendMessage()} disabled={!input.trim()||loading}>
                {loading?<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>}
              </button>
            </div>
          </div>
          <p className="disclaimer">MuriAI · {messages.length>0&&<span className="export-inline" onClick={exportChat}>Export chat ↓</span>}</p>
        </div>
      </main>

      {showPlans && (
        <PremiumPlans
          username={currentUser}
          currentSubscription={subscription}
          onSubscribed={(data) => {
            setSubscription({ plan_name: data.plan, chats_used: 0, chat_limit: -1 });
            setShowPlans(false);
          }}
          onClose={() => setShowPlans(false)}
        />
      )}

      {/* Agent Deactivated Popup */}
      {showAgentDeactivatedPopup && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, backdropFilter: "blur(4px)"
        }}>
          <div style={{
            background: "#1a1d2e", border: "1px solid rgba(248,113,113,0.4)",
            borderRadius: 16, padding: "28px 24px", maxWidth: 400, width: "90%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
          }}>
            <div style={{ fontSize: 32, textAlign: "center", marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: "0 0 8px", textAlign: "center", color: "#f87171", fontSize: 16 }}>
              Agent Deactivated
            </h3>
            <p style={{ margin: "0 0 20px", textAlign: "center", color: "#94a3b8", fontSize: 13, lineHeight: 1.5 }}>
              <strong style={{ color: "#e2e8f0" }}>{activeAgentName}</strong> has been deactivated by the admin.
              {availableAgentsForSwitch.length > 0
                ? " Would you like to switch to another agent?"
                : " There are no other active agents available right now."}
            </p>

            {availableAgentsForSwitch.length > 0 ? (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {availableAgentsForSwitch.map(agent => (
                    <button key={agent.id} onClick={() => {
                      setSelectedAgentKey(agent.id);
                      setActiveAgentName(agent.name);
                      setShowAgentDeactivatedPopup(false);
                    }} style={{
                      padding: "12px 16px", background: "rgba(99,102,241,0.15)",
                      border: "1.5px solid rgba(99,102,241,0.4)", borderRadius: 10,
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left"
                    }}>
                      <span style={{ fontSize: 24 }}>{agent.icon || "🤖"}</span>
                      <div>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#e2e8f0" }}>{agent.name}</p>
                        {agent.desc && <p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>{agent.desc}</p>}
                      </div>
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowAgentDeactivatedPopup(false)} style={{
                  width: "100%", padding: "9px 0", background: "transparent",
                  border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
                  color: "#64748b", cursor: "pointer", fontSize: 12
                }}>
                  Dismiss (stay on current chat)
                </button>
              </>
            ) : (
              <button onClick={() => setShowAgentDeactivatedPopup(false)} style={{
                width: "100%", padding: "10px 0", background: "rgba(99,102,241,0.2)",
                border: "1px solid rgba(99,102,241,0.4)", borderRadius: 8,
                color: "#a5b4fc", cursor: "pointer", fontSize: 13, fontWeight: 600
              }}>
                OK, I understand
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}